/**
 * @file bbd-datalayer-events.js
 * @description GA4 non-ecommerce event tracking via dataLayer.
 * Handles navigation, filter, account, search, newsletter, and other custom events.
 *
 * Design principle: prefer class/element-based detection over data attributes so
 * tracking works automatically without per-section Liquid changes. Data attributes
 * are used only where explicit opt-in per instance is needed (e.g. homepage CTAs
 * that also appear on other page types).
 *
 * GTM-only events (no theme code needed):
 * - navigation_footer (GTM click trigger on footer links)
 * - click_mailto / copy_email
 * - click_phonenumber / copy_phonenumber
 * - engaged_visitor (GTM timer trigger)
 * - search_results_interaction (GTM click trigger)
 * - retour_label (Loop Returns — GTM click trigger)
 * - download (GA4 Enhanced Measurement handles file_download automatically)
 *
 * NOTE: form_start / form_submit were previously listed here as GTM-only.
 * Moved to a dataLayer.push implementation (see initFormTracking) per the
 * GA4 measurement plan, which expects named form_name/form_location
 * parameters. If a GTM native Form Submission trigger for form_submit
 * already exists, check GTM for duplicate firing.
 */

const push = (eventObj) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventObj);
};

// ---------------------------------------------------------------------------
// Navigation — main menu, submenu, header buttons
// data-datalayer-nav="main_menu|submenu|header_button" added in header Liquid
// data-datalayer-nav-placement="header"
// data-datalayer-nav-category="Armbanden"
//
// Breadcrumbs (back-link + collection breadcrumb) are tracked generically by
// class — no data attributes needed in Liquid.
// ---------------------------------------------------------------------------
const initNavigationTracking = () => {
  document.addEventListener('click', (e) => {
    // Data-attribute based navigation (menus, header buttons)
    const link = e.target.closest('[data-datalayer-nav]');
    if (link) {
      const navType = link.dataset.datalayerNav;
      const placement = link.dataset.datalayerNavPlacement ?? 'header';
      const category = link.dataset.datalayerNavCategory ?? '';

      const eventMap = {
        main_menu: 'navigation_main_menu',
        submenu: 'navigation_submenu',
        header_button: 'navigation_header_buttons',
        breadcrumb: 'navigation_breadcrumbs',
        product_tab: 'navigation_product_tabs',
        sidebar: 'navigation_sidebar'
      };

      const eventName = eventMap[navType];
      if (eventName) {
        const payload = {
          event: eventName,
          placement,
          click_text: link.textContent.trim(),
          click_url: link.href ?? ''
        };
        if (category) payload.category = category;
        push(payload);
      }
      return;
    }

    // Generic breadcrumb tracking — covers bbd-pdp-breadcrumbs (.back-link) and
    // bbd-back-link (.collection-breadcrumb__link). No data attributes required.
    const breadcrumb = e.target.closest('.back-link, .collection-breadcrumb__link');
    if (breadcrumb) {
      push({
        event: 'navigation_breadcrumbs',
        placement: 'pdp',
        click_text:
          breadcrumb.querySelector('.back-link__label, .bbd-pdp-breadcrumbs__back-text')?.textContent.trim() ??
          breadcrumb.textContent.trim(),
        click_url: breadcrumb.href ?? ''
      });
    }
  });
};

// ---------------------------------------------------------------------------
// view_search_results — on search page load
// ---------------------------------------------------------------------------
const initViewSearchResults = () => {
  if (!document.querySelector('[data-datalayer-list-name="search"]')) return;

  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get('q') ?? '';

  push({
    event: 'view_search_results',
    search_term: searchTerm
  });
};

// ---------------------------------------------------------------------------
// overview_filter / interactie_filter — filter interactions
//
// Two sources are covered:
// 1. change events on facet-inputs-component (filter checkboxes/swatches/pills)
//    and sorting-filter-component (sort radio/select).
//    The native change event bubbles to document; no stopPropagation is called
//    by the component system, so we can read the changed input before the
//    section re-renders.
// 2. [data-datalayer-filter] — explicit opt-in for any other direct filter links.
//
// Note: .quick-filter links are tracked as category_click (see initCategoryClick),
// not as filter events, since they are category navigation rather than refinements.
// ---------------------------------------------------------------------------
const initFilterTracking = () => {
  document.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    // Facet filter interactions (facet-inputs-component)
    if (target.closest('facet-inputs-component')) {
      const label = target.closest('ul[name]')?.getAttribute('name') ?? '';
      const value =
        target.dataset.label ||
        target.getAttribute('aria-label') ||
        document.querySelector(`label[for="${target.id}"]`)?.textContent.trim() ||
        target.value;

      push({ event: 'overview_filter', action: 'filter', label, value, state: target.checked ? 'on' : 'off' });
      return;
    }

    // Sort interactions (sorting-filter-component)
    if (target.closest('sorting-filter-component')) {
      const value =
        target instanceof HTMLSelectElement
          ? (target.options[target.selectedIndex]?.text ?? target.value)
          : target.dataset.optionName || target.value;

      push({ event: 'overview_filter', action: 'sort', label: 'Sortering', value });
    }
  });

  // Explicit opt-in filter links (data attribute)
  document.addEventListener('click', (e) => {
    const filterLink = e.target.closest('[data-datalayer-filter]');
    if (!filterLink) return;

    push({
      event: 'interactie_filter',
      click_text: filterLink.textContent.trim(),
      category: filterLink.dataset.datalayerFilterCategory ?? ''
    });
  });
};

// ---------------------------------------------------------------------------
// country_list_select — market/locale switcher
// data-datalayer-country-select added to bbd-localization-select.liquid
// ---------------------------------------------------------------------------
const initCountryListSelect = () => {
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-datalayer-country-select]');
    if (!select) return;

    push({
      event: 'country_list_select',
      click_text: select.options[select.selectedIndex]?.text ?? ''
    });
  });
};

// ---------------------------------------------------------------------------
// login / sign_up — account events
// Fired from <script id="bbd-datalayer-account"> JSON block in account templates
// Also listens to Shopify's new customer accounts shop-login-button completed event
// Also listens to clicks on the .account-actions__link fallback sign-in link
// ---------------------------------------------------------------------------
const initAccountEvents = () => {
  // Classic customer accounts: JSON block in template on login success page
  const scriptEl = document.getElementById('bbd-datalayer-account');
  if (scriptEl) {
    try {
      const { event: eventName } = JSON.parse(scriptEl.textContent);
      if (eventName) {
        push({ event: eventName });
      }
    } catch {
      // not an account page
    }
  }

  // New customer accounts: shop-login-button completed event.
  // No nested DOMContentLoaded needed — this function is already called from DOMContentLoaded.
  const shopLoginBtn = document.querySelector('shop-login-button');
  if (!shopLoginBtn) return;

  shopLoginBtn.addEventListener('completed', () => {
    push({
      event: 'login'
    });
  });

  // Fallback sign-in link inside .account-actions__sign-ins (account-actions snippet).
  const signInLink = document.querySelector('.account-actions__sign-ins .account-actions__link');
  if (signInLink) {
    signInLink.addEventListener('click', () => {
      push({
        event: 'login'
      });
    });
  }
};

// ---------------------------------------------------------------------------
// newsletter_subscription — newsletter form submit
// Listens to two event sources:
// 1. 'newsletter:subscribed' — custom event dispatched by the theme's native form handler
// 2. 'klaviyoForms' — window event dispatched by Klaviyo embedded forms on submit
// ---------------------------------------------------------------------------
const initNewsletterTracking = () => {
  const fireNewsletterEvent = () => {
    push({
      event: 'newsletter_subscription'
    });
  };

  // Theme native forms
  document.addEventListener('newsletter:subscribed', fireNewsletterEvent);

  // Klaviyo embedded forms — detail.type is 'open' | 'close' | 'submit' | 'stepSubmit' | 'redirectedToUrl'
  window.addEventListener('klaviyoForms', (e) => {
    if (e.detail?.type === 'submit') {
      fireNewsletterEvent();
    }
  });
};

// ---------------------------------------------------------------------------
// review_sidebar — Trustpilot sidebar open
// data-datalayer-review-sidebar added to the desktop TrustBox widget in
// sections/bbd-trustpilot-store-reviews.liquid
// ---------------------------------------------------------------------------
const initReviewSidebar = () => {
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-datalayer-review-sidebar]');
    if (!trigger) return;

    push({
      event: 'review_sidebar'
    });
  });
};

// ---------------------------------------------------------------------------
// homepage_buttons / homepage_categories
//
// Both are detected generically — no data attributes needed in Liquid.
// Guarded by isHomepage() so events only fire on the homepage even when the
// same sections are reused on other page types.
//
// homepage_buttons:
//   Every .button click inside #MainContent on the homepage.
//
// homepage_categories:
//   Links inside any section whose Shopify-rendered ID contains 'bbd_collections'
//   (e.g. id="shopify-section-bbd-collections-..."). This covers the bbd-collections
//   section wherever it is placed.
// ---------------------------------------------------------------------------

/**
 * Returns true if the current page is the homepage, based on page_type pushed
 * to dataLayer by bbd-datalayer-init.liquid.
 *
 * @returns {boolean}
 */
const isHomepage = () => (window.dataLayer ?? []).some((entry) => entry.page_type === 'home');

const initHomepageTracking = () => {
  if (!isHomepage()) return;

  const mainContent = document.getElementById('MainContent');
  if (!mainContent) return;

  mainContent.addEventListener('click', (e) => {
    // homepage_categories — links inside the bbd-collections section
    const collectionsSection = e.target.closest('[id*="bbd_collections"]');
    if (collectionsSection) {
      const link = e.target.closest('a');
      if (link) {
        push({
          event: 'homepage_categories',
          click_text: link.textContent.trim(),
          click_url: link.href ?? ''
        });
        return;
      }
    }

    // homepage_buttons — any .button inside MainContent, excluding add-to-cart
    // buttons (tracked separately as add_to_cart in bbd-datalayer-ecommerce.js)
    const btn = e.target.closest('.button:not(.add-to-cart-button):not([name="add"])');
    if (btn) {
      push({
        event: 'homepage_buttons',
        click_text: btn.textContent.trim(),
        click_url: btn.href ?? btn.closest('a')?.href ?? ''
      });
    }
  });
};

// ---------------------------------------------------------------------------
// category_click — category navigation links
//
// Two sources are covered generically — no data attributes needed:
// 1. .quick-filter links inside .bbd-collection-quick-filters sections.
//    These are collection navigation tiles placed on collection/PDP pages.
// 2. [data-datalayer-category-click] — explicit opt-in for other category links.
// ---------------------------------------------------------------------------
const initCategoryClick = () => {
  document.addEventListener('click', (e) => {
    // Quick filters (bbd-collection-quick-filters) — class-based
    const quickFilter = e.target.closest('.bbd-collection-quick-filters .quick-filter');
    if (quickFilter) {
      push({
        event: 'category_click',
        click_text: quickFilter.querySelector('.quick-filter__title')?.textContent.trim() ?? '',
        click_url: quickFilter.href ?? ''
      });
      return;
    }

    // Explicit opt-in
    const el = e.target.closest('[data-datalayer-category-click]');
    if (el) {
      push({
        event: 'category_click',
        click_text: el.textContent.trim(),
        click_url: el.href ?? el.closest('a')?.href ?? ''
      });
    }
  });
};

// ---------------------------------------------------------------------------
// product_details_expand — accordion open on PDP
//
// Tracked generically via the toggle event on <details class="details"> inside
// product-information-component. Covers all _accordion-row blocks on PDP without
// requiring data attributes on the base theme block file.
//
// Only fires on open (not close). Heading text is extracted by cloning the
// <summary> and stripping icon/SVG child elements.
// ---------------------------------------------------------------------------
const initProductDetailsExpand = () => {
  document.addEventListener(
    'toggle',
    (e) => {
      const details = e.target.closest('details.details');
      if (!details || !details.open) return;
      if (!details.closest('product-information-component')) return;

      const summaryEl = details.querySelector('summary.details__header');
      if (!summaryEl) return;

      // Strip decorative child elements (SVG icons, images) to get the heading text
      const clone = summaryEl.cloneNode(true);
      clone.querySelectorAll('.svg-wrapper, .details__icon, img').forEach((el) => el.remove());
      const clickText = clone.textContent.trim();

      push({
        event: 'product_details_expand',
        click_text: clickText
      });
    },
    true
  ); // capture phase — toggle does not bubble
};

// ---------------------------------------------------------------------------
// maat_check — "Wat is mijn maat" button on PDP
// data-datalayer-maat-check added to the sizing guide trigger in
// snippets/bbd-variant-picker-dropdown.liquid
// ---------------------------------------------------------------------------
const initMaatCheck = () => {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-datalayer-maat-check]');
    if (!el) return;

    push({
      event: 'maat_check'
    });
  });
};

// ---------------------------------------------------------------------------
// geef_me_een_seintje — OoS notification sign-up
//
// Two sources are covered:
// 1. #klaviyo-bis-button-container — Klaviyo back-in-stock form, dynamically
//    injected when a product is out of stock. Tracked via event delegation so
//    no MutationObserver is needed; the listener fires when the user clicks
//    any button/submit inside the container regardless of when it was injected.
// 2. [data-datalayer-oos-notify] — explicit opt-in for other OoS triggers.
// ---------------------------------------------------------------------------
const initOosNotify = () => {
  document.addEventListener('click', (e) => {
    // Klaviyo BIS form — dynamically injected, caught via delegation
    const bisContainer = document.getElementById('klaviyo-bis-button-container');
    if (bisContainer?.contains(e.target)) {
      const btn = e.target.closest('button, [type="submit"]');
      if (btn) {
        push({
          event: 'geef_me_een_seintje',
          click_text: btn.textContent.trim()
        });
        return;
      }
    }

    // Explicit opt-in
    const el = e.target.closest('[data-datalayer-oos-notify]');
    if (el) {
      push({
        event: 'geef_me_een_seintje',
        click_text: el.textContent.trim(),
        click_url: el.href ?? ''
      });
    }
  });
};

// ---------------------------------------------------------------------------
// product_view_image — image gallery interaction on PDP
// Fired from slideshow:select event on product-information-component
// ---------------------------------------------------------------------------
const initProductViewImage = () => {
  const scriptEl = document.getElementById('bbd-datalayer-product');
  if (!scriptEl) return;

  const sectionEl = scriptEl.closest('[id^="shopify-section-"]');
  const mainProductInfoEl = sectionEl?.querySelector('product-information-component');
  const mainGallerySlideshowEl = mainProductInfoEl?.querySelector('.product-information__media slideshow-component');

  if (!mainProductInfoEl || !mainGallerySlideshowEl) return;

  let productData;
  try {
    productData = JSON.parse(scriptEl.textContent);
  } catch {
    return;
  }

  document.addEventListener('slideshow:select', (e) => {
    // only events from the main PDP media gallery slideshow
    if (e.target !== mainGallerySlideshowEl) return;

    const index = Number(e.detail?.index ?? 0);
    const previousIndex = Number(e.detail?.previousIndex ?? -1);
    if (index === previousIndex) return;

    push({
      event: 'product_view_image',
      position: index,
      item_name: productData.product?.title ?? '',
      item_category: productData.product?.type ?? ''
    });
  });
};

// ---------------------------------------------------------------------------
// cart_coupon / cart_coupon_remove — coupon code events
// Fired via discount:update custom event dispatched from cart-discount-component
// ---------------------------------------------------------------------------
const initCartCouponTracking = () => {
  document.addEventListener('discount:update', (e) => {
    const detail = e.detail ?? {};
    if (detail.removed) {
      push({
        event: 'cart_coupon_remove'
      });
    } else {
      push({
        event: 'cart_coupon'
      });
    }
  });
};

// ---------------------------------------------------------------------------
// cart_empty — empty cart page view
//
// Detected generically via the .cart-page--empty modifier class that
// sections/main-cart.liquid applies when cart.empty? is true.
// No data attribute required in Liquid.
// ---------------------------------------------------------------------------
const initCartEmptyTracking = () => {
  if (!document.querySelector('.cart-page--empty')) return;

  push({
    event: 'cart_empty'
  });
};

// ---------------------------------------------------------------------------
// form_start / form_submit — contact form and blog comment form
//
// Detected generically via Shopify's auto-inserted `form_type` hidden field
// (present on every {% form %} tag), so no data attributes are needed.
// Scoped to 'contact' and 'new_comment' form types only — product/cart forms
// (add_to_cart), the newsletter form (newsletter_subscription), and account
// forms (login) are tracked separately and must not double up here.
//
// form_location uses the page_type already pushed by bbd-datalayer-init.liquid.
// ---------------------------------------------------------------------------
const FORM_NAME_BY_TYPE = {
  contact: 'contact',
  new_comment: 'blog_comment'
};

const formStartFired = new WeakSet();

const getFormLocation = () => (window.dataLayer ?? []).find((entry) => entry.page_type)?.page_type ?? '';

const getTrackedFormName = (form) => {
  if (!(form instanceof HTMLFormElement)) return null;
  const formType = form.querySelector('input[name="form_type"]')?.value;
  return formType ? (FORM_NAME_BY_TYPE[formType] ?? null) : null;
};

const initFormTracking = () => {
  document.addEventListener('focusin', (e) => {
    const form = e.target.closest('form');
    if (!form || formStartFired.has(form)) return;

    const formName = getTrackedFormName(form);
    if (!formName) return;

    formStartFired.add(form);
    push({
      event: 'form_start',
      form_name: formName,
      form_location: getFormLocation()
    });
  });

  document.addEventListener('submit', (e) => {
    const formName = getTrackedFormName(e.target);
    if (!formName) return;

    push({
      event: 'form_submit',
      form_name: formName,
      form_location: getFormLocation()
    });
  });
};

// ---------------------------------------------------------------------------
// promotion_banner_close — close button on bbd-promotion-banner (header group)
// ---------------------------------------------------------------------------
const initPromotionBannerClose = () => {
  document.addEventListener('click', (e) => {
    const closeButton = e.target.closest('.bbd-promotion-banner__close');
    if (!closeButton) return;

    const banner = closeButton.closest('.bbd-promotion-banner');
    if (!banner) return;

    push({
      event: 'promotion_banner_close',
      promotion_id: banner.dataset.datalayerPromotionId ?? '',
      promotion_name: banner.dataset.datalayerPromotionName ?? ''
    });
  });
};

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  initNavigationTracking();
  initViewSearchResults();
  initFilterTracking();
  initCountryListSelect();
  initAccountEvents();
  initNewsletterTracking();
  initReviewSidebar();
  initHomepageTracking();
  initCategoryClick();
  initProductDetailsExpand();
  initMaatCheck();
  initOosNotify();
  initProductViewImage();
  initCartCouponTracking();
  initCartEmptyTracking();
  initFormTracking();
  initPromotionBannerClose();
});
